/**
 * Tela /imports — wizard multi-step de upload de planilha (M2.E7.S1).
 *
 * - S1.1 (TH-53210, ✅): Step 1 — selecionar loja + mapeamento.
 * - S1.2 (TH-53211, ✅): Step 2 — upload com drag & drop.
 * - S1.3 (TH-53212, ✅): Step 3 — preview com tabela e edição inline.
 * - S1.4 (TH-53213, esta issue): Step 4 — confirmação de apply.
 *
 * Estado interno via useState neste passo. Quando os steps seguintes
 * entrarem, vale extrair um Context dedicado.
 *
 * Nota sobre import_mappings: o backend (apps/api/.../import_mappings.py)
 * lista mappings por tenant, sem filtro `store_id` — não há associação
 * mapping↔store no schema. O DoD original mencionava filtro por
 * store_id; aqui mostramos todos os mappings do tenant (correto pelo
 * schema atual).
 */

import { useMemo, useRef, useState, type ChangeEvent, type DragEvent } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { History } from "lucide-react";

import { AppLayout } from "@/components/AppLayout";
import { RequireAuth } from "@/components/RequireAuth";
import { Button } from "@/components/ui/Button";
import { Label } from "@/components/ui/Label";
import { Select } from "@/components/ui/Select";
import { useAuth } from "@/hooks/useAuth";
import { ImportsStep3Preview } from "@/pages/imports/ImportsStep3Preview";
import { ImportsStep4Confirm } from "@/pages/imports/ImportsStep4Confirm";

interface StoreOption {
  id: string;
  name: string;
  code: string | null;
}

interface PagedStoreResponse {
  data: StoreOption[];
  meta: { page: number; per_page: number; total: number };
}

interface ImportMapping {
  id: string;
  name: string;
}

interface ImportBatchCreated {
  id: string;
  status: string;
  file_url: string;
  store_id: string | null;
  mapping_id: string | null;
  created_at: string;
}

async function fetchStores(): Promise<StoreOption[]> {
  // per_page=100 é o limite atual do backend (`stores.py: Query(..., le=100)`).
  // Tenants com >100 lojas truncam silenciosamente o dropdown — endpoint
  // lightweight `/stores/all?fields=id,name` é o follow-up correto. TH-55119.
  const res = await fetch("/api/stores?page=1&per_page=100", {
    credentials: "include",
  });
  if (!res.ok) {
    throw new Error(`HTTP ${res.status}`);
  }
  const body = (await res.json()) as PagedStoreResponse;
  return body.data;
}

async function fetchMappings(): Promise<ImportMapping[]> {
  // GET /import-mappings retorna list[ImportMappingRead] direto (sem envelope).
  const res = await fetch("/api/import-mappings", { credentials: "include" });
  if (!res.ok) {
    throw new Error(`HTTP ${res.status}`);
  }
  return (await res.json()) as ImportMapping[];
}

const ROLES_AUTHORIZED = new Set([
  "super_admin",
  "tenant_admin",
  "store_manager",
  "operator",
]);

// Backend (apps/api/.../routers/imports.py) aceita .xlsx, .xls, .csv até 10 MB.
// Mantemos o mesmo limite no client para falhar cedo sem subir bytes.
const ACCEPTED_EXTS = [".xlsx", ".xls", ".csv"] as const;
const ACCEPT_ATTR = ACCEPTED_EXTS.join(",");
const MAX_BYTES = 10 * 1024 * 1024;

type WizardStep = 1 | 2 | 3 | 4;

interface WizardState {
  storeId: string;
  mappingId: string;
  file: File | null;
  batchId: string | null;
}

function getExt(filename: string): string {
  const idx = filename.lastIndexOf(".");
  return idx >= 0 ? filename.slice(idx).toLowerCase() : "";
}

function validateFile(file: File): string | null {
  const ext = getExt(file.name);
  if (!ACCEPTED_EXTS.includes(ext as (typeof ACCEPTED_EXTS)[number])) {
    return `extensão ${ext || "?"} não suportada — use xlsx, xls ou csv`;
  }
  if (file.size > MAX_BYTES) {
    return `arquivo excede 10 MB (${(file.size / 1024 / 1024).toFixed(1)} MB)`;
  }
  if (file.size === 0) {
    return "arquivo vazio";
  }
  return null;
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

export function ImportsPage() {
  const { user } = useAuth();
  const canRead = user ? ROLES_AUTHORIZED.has(user.role) : false;

  const [currentStep, setCurrentStep] = useState<WizardStep>(1);
  const [wizard, setWizard] = useState<WizardState>({
    storeId: "",
    mappingId: "",
    file: null,
    batchId: null,
  });

  const storesQuery = useQuery({
    queryKey: ["stores", "all-for-imports"],
    queryFn: fetchStores,
    staleTime: 30_000,
    enabled: canRead,
  });

  const mappingsQuery = useQuery({
    queryKey: ["import-mappings"],
    queryFn: fetchMappings,
    staleTime: 30_000,
    enabled: canRead,
  });

  // Resolução dos nomes para exibir no Step 4 — derivados das queries
  // (sem fetch extra). Mostramos `code · name` para a loja, alinhando
  // com o select do Step 1.
  const selectedStoreName = useMemo(() => {
    const store = (storesQuery.data ?? []).find(
      (s) => s.id === wizard.storeId,
    );
    if (!store) return undefined;
    return store.code ? `${store.code} · ${store.name}` : store.name;
  }, [storesQuery.data, wizard.storeId]);

  const selectedMappingName = useMemo(() => {
    return (mappingsQuery.data ?? []).find((m) => m.id === wizard.mappingId)
      ?.name;
  }, [mappingsQuery.data, wizard.mappingId]);

  if (user && !canRead) {
    return (
      <RequireAuth>
        <AppLayout
          breadcrumb="OPERAÇÃO / IMPORTAÇÕES"
          title="Acesso negado"
        >
          <div className="mx-auto max-w-3xl">
            <p className="text-sm text-[var(--color-muted-foreground)]">
              Esta tela está disponível para super_admin, tenant_admin,
              store_manager ou operator.
            </p>
          </div>
        </AppLayout>
      </RequireAuth>
    );
  }

  return (
    <RequireAuth>
      <AppLayout
        breadcrumb="OPERAÇÃO / IMPORTAÇÕES"
        title="Nova importação"
        actions={
          <Link
            to="/imports/history"
            className="inline-flex h-9 items-center gap-2 rounded-md border border-[var(--color-border)] px-3 text-sm font-medium hover:bg-[var(--color-muted-tint)]"
          >
            <History className="h-4 w-4" aria-hidden="true" />
            Ver histórico
          </Link>
        }
      >
        {/* Step 3 tem tabela larga; demais steps cabem em coluna estreita. */}
        <div
          className={
            currentStep === 3
              ? "mx-auto max-w-6xl"
              : "mx-auto max-w-2xl"
          }
        >
          {currentStep === 1 ? (
            <Step1SelectStore
              wizard={wizard}
              setWizard={setWizard}
              storesQuery={storesQuery}
              mappingsQuery={mappingsQuery}
              onNext={() => setCurrentStep(2)}
            />
          ) : currentStep === 2 ? (
            <Step2Upload
              wizard={wizard}
              setWizard={setWizard}
              onBack={() => setCurrentStep(1)}
              onNext={(batchId) => {
                setWizard((prev) => ({ ...prev, batchId }));
                setCurrentStep(3);
              }}
            />
          ) : currentStep === 3 && wizard.batchId ? (
            <ImportsStep3Preview
              batchId={wizard.batchId}
              onBack={() => setCurrentStep(2)}
              onNext={() => setCurrentStep(4)}
            />
          ) : currentStep === 4 && wizard.batchId ? (
            <ImportsStep4Confirm
              batchId={wizard.batchId}
              storeName={selectedStoreName}
              mappingName={selectedMappingName}
              onBack={() => setCurrentStep(3)}
              onDone={() => {
                setCurrentStep(1);
                setWizard({
                  storeId: "",
                  mappingId: "",
                  file: null,
                  batchId: null,
                });
              }}
            />
          ) : (
            // Salva-vidas: caímos aqui só se faltar batchId em Step 3/4
            // (estado inválido). Volta o usuário ao começo do wizard.
            <NoBatchFallback onReset={() => setCurrentStep(1)} />
          )}
        </div>
      </AppLayout>
    </RequireAuth>
  );
}

interface Step1Props {
  wizard: WizardState;
  setWizard: React.Dispatch<React.SetStateAction<WizardState>>;
  storesQuery: ReturnType<typeof useQuery<StoreOption[], Error>>;
  mappingsQuery: ReturnType<typeof useQuery<ImportMapping[], Error>>;
  onNext: () => void;
}

function Step1SelectStore({
  wizard,
  setWizard,
  storesQuery,
  mappingsQuery,
  onNext,
}: Step1Props) {
  const canProceed = Boolean(wizard.storeId) && Boolean(wizard.mappingId);

  return (
    <>
      <div className="text-[10px] font-semibold uppercase tracking-[0.1em] text-[var(--color-muted-foreground)]">
        Passo 1 de 4 · Loja e mapeamento
      </div>
      <p className="mt-2 text-sm text-[var(--color-muted-foreground)]">
        Escolha a loja que receberá os preços e o mapeamento que descreve
        as colunas da sua planilha. Os próximos passos são upload, preview
        e confirmação.
      </p>

      <form
        className="mt-6 flex flex-col gap-5"
        onSubmit={(e) => {
          e.preventDefault();
          if (canProceed) onNext();
        }}
      >
        <div className="flex flex-col gap-2">
          <Label htmlFor="store">Loja</Label>
          <Select
            id="store"
            value={wizard.storeId}
            onChange={(e) =>
              setWizard((prev) => ({ ...prev, storeId: e.target.value }))
            }
            disabled={storesQuery.isLoading || !!storesQuery.error}
          >
            <option value="">
              {storesQuery.isLoading
                ? "carregando lojas…"
                : "selecione uma loja"}
            </option>
            {(storesQuery.data ?? []).map((s) => (
              <option key={s.id} value={s.id}>
                {s.code ? `${s.code} · ${s.name}` : s.name}
              </option>
            ))}
          </Select>
          {storesQuery.error ? (
            <p className="text-xs text-[var(--color-destructive)]">
              erro ao carregar lojas
            </p>
          ) : null}
        </div>

        <div className="flex flex-col gap-2">
          <Label htmlFor="mapping">Mapeamento da planilha</Label>
          <Select
            id="mapping"
            value={wizard.mappingId}
            onChange={(e) =>
              setWizard((prev) => ({ ...prev, mappingId: e.target.value }))
            }
            disabled={mappingsQuery.isLoading || !!mappingsQuery.error}
          >
            <option value="">
              {mappingsQuery.isLoading
                ? "carregando mapeamentos…"
                : "selecione um mapeamento"}
            </option>
            {(mappingsQuery.data ?? []).map((m) => (
              <option key={m.id} value={m.id}>
                {m.name}
              </option>
            ))}
          </Select>
          {mappingsQuery.error ? (
            <p className="text-xs text-[var(--color-destructive)]">
              erro ao carregar mapeamentos
            </p>
          ) : null}
          {!mappingsQuery.isLoading &&
          !mappingsQuery.error &&
          (mappingsQuery.data ?? []).length === 0 ? (
            <p className="text-xs text-[var(--color-muted-foreground)]">
              nenhum mapeamento cadastrado — crie um em Configurações
              antes de prosseguir.
            </p>
          ) : null}
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button type="submit" disabled={!canProceed}>
            Próximo →
          </Button>
        </div>
      </form>
    </>
  );
}

interface Step2Props {
  wizard: WizardState;
  setWizard: React.Dispatch<React.SetStateAction<WizardState>>;
  onBack: () => void;
  onNext: (batchId: string) => void;
}

function Step2Upload({ wizard, setWizard, onBack, onNext }: Step2Props) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  function setFile(file: File | null) {
    setUploadError(null);
    if (file === null) {
      setWizard((prev) => ({ ...prev, file: null }));
      setValidationError(null);
      return;
    }
    const err = validateFile(file);
    if (err) {
      setValidationError(err);
      setWizard((prev) => ({ ...prev, file: null }));
      return;
    }
    setValidationError(null);
    setWizard((prev) => ({ ...prev, file }));
  }

  function handleInputChange(e: ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0] ?? null;
    setFile(f);
    // Permite reselecionar o mesmo arquivo após "Trocar".
    if (inputRef.current) inputRef.current.value = "";
  }

  function handleDragOver(e: DragEvent<HTMLDivElement>) {
    e.preventDefault();
    e.stopPropagation();
    if (!isDragging) setIsDragging(true);
  }

  function handleDragLeave(e: DragEvent<HTMLDivElement>) {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }

  function handleDrop(e: DragEvent<HTMLDivElement>) {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const f = e.dataTransfer.files?.[0] ?? null;
    if (f) setFile(f);
  }

  function openPicker() {
    inputRef.current?.click();
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLDivElement>) {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      openPicker();
    }
  }

  async function handleUpload() {
    if (!wizard.file) return;
    setUploading(true);
    setUploadError(null);
    try {
      const fd = new FormData();
      fd.append("file", wizard.file);
      fd.append("store_id", wizard.storeId);
      fd.append("mapping_id", wizard.mappingId);
      const res = await fetch("/api/imports", {
        method: "POST",
        body: fd,
        credentials: "include",
      });
      if (!res.ok) {
        let detail = `HTTP ${res.status}`;
        try {
          const body = (await res.json()) as { detail?: string };
          if (body.detail) detail = body.detail;
        } catch {
          // resposta não-json — mantém HTTP <status>
        }
        setUploadError(detail);
        return;
      }
      const body = (await res.json()) as ImportBatchCreated;
      onNext(body.id);
    } catch (err) {
      setUploadError(
        err instanceof Error ? err.message : "falha ao enviar arquivo",
      );
    } finally {
      setUploading(false);
    }
  }

  const canProceed = Boolean(wizard.file) && !uploading;

  return (
    <>
      <div className="text-[10px] font-semibold uppercase tracking-[0.1em] text-[var(--color-muted-foreground)]">
        Passo 2 de 4 · Enviar planilha
      </div>
      <p className="mt-2 text-sm text-[var(--color-muted-foreground)]">
        Solte sua planilha aqui ou clique para escolher. Aceitamos xlsx,
        xls e csv até 10 MB.
      </p>

      <div className="mt-6 flex flex-col gap-4">
        <input
          ref={inputRef}
          type="file"
          accept={ACCEPT_ATTR}
          onChange={handleInputChange}
          className="sr-only"
          aria-hidden="true"
          tabIndex={-1}
        />

        {wizard.file ? (
          <div className="flex items-center justify-between gap-4 rounded-md border border-[var(--color-border-strong)] bg-[var(--color-secondary)] p-4">
            <div className="flex flex-col gap-1 overflow-hidden">
              <div className="truncate text-sm font-medium text-[var(--color-foreground)]">
                {wizard.file.name}
              </div>
              <div className="text-xs text-[var(--color-muted-foreground)]">
                {formatBytes(wizard.file.size)} ·{" "}
                {wizard.file.type || "tipo não detectado"}
              </div>
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setFile(null)}
              disabled={uploading}
            >
              Trocar
            </Button>
          </div>
        ) : (
          <div
            role="button"
            tabIndex={0}
            aria-label="área de upload — solte ou clique para escolher arquivo"
            onClick={openPicker}
            onKeyDown={handleKeyDown}
            onDragOver={handleDragOver}
            onDragEnter={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            className={[
              "flex cursor-pointer flex-col items-center justify-center gap-2 rounded-md border-2 border-dashed p-10 text-center transition-colors",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-ring)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--color-background)]",
              isDragging
                ? "border-[var(--color-primary)] bg-[var(--color-primary)]/10"
                : "border-[var(--color-border-strong)] hover:border-[var(--color-primary)]/60 hover:bg-[var(--color-secondary)]",
            ].join(" ")}
          >
            <div className="text-sm font-medium text-[var(--color-foreground)]">
              {isDragging
                ? "solte aqui para usar este arquivo"
                : "arraste sua planilha ou clique para selecionar"}
            </div>
            <div className="text-xs text-[var(--color-muted-foreground)]">
              xlsx · xls · csv · até 10 MB
            </div>
          </div>
        )}

        {validationError ? (
          <p
            role="alert"
            className="text-xs text-[var(--color-destructive)]"
          >
            {validationError}
          </p>
        ) : null}

        {uploadError ? (
          <div
            role="alert"
            className="rounded-md border border-[var(--color-destructive)]/40 bg-[var(--color-destructive)]/10 p-3 text-xs text-[var(--color-destructive)]"
          >
            {uploadError}
          </div>
        ) : null}

        <div className="flex justify-between gap-2 pt-2">
          <Button
            type="button"
            variant="outline"
            onClick={onBack}
            disabled={uploading}
          >
            ← Voltar
          </Button>
          <Button
            type="button"
            onClick={handleUpload}
            disabled={!canProceed}
          >
            {uploading ? "Enviando…" : "Próximo →"}
          </Button>
        </div>
      </div>
    </>
  );
}

interface NoBatchFallbackProps {
  onReset: () => void;
}

function NoBatchFallback({ onReset }: NoBatchFallbackProps) {
  return (
    <div className="flex flex-col gap-3">
      <div className="text-[10px] font-semibold uppercase tracking-[0.1em] text-[var(--color-muted-foreground)]">
        Erro de estado
      </div>
      <div className="rounded-md border border-[var(--color-destructive)]/40 bg-[var(--color-destructive)]/10 p-4 text-sm text-[var(--color-destructive)]">
        <p>
          Nenhum batch ativo no wizard. Recomece a importação para continuar.
        </p>
      </div>
      <div className="flex justify-end">
        <Button type="button" onClick={onReset}>
          Recomeçar
        </Button>
      </div>
    </div>
  );
}
